/* eslint-disable @reatom/unit-naming-rule */
import {
  action,
  atom,
  onConnect,
  reaction,
  reatomAsync,
  sleep,
  withAbort,
} from '@reatom/framework'
import { nanoid } from 'nanoid'

import { AsyncStateN, pipe, Result } from '@blade/std-lib'
import { fetchQuoteRequestAction } from '@root/entities/quote/atoms'
import { newTxEvent } from '@root/entities/realtime/atoms/new-tx-event'
import { joinedInviteCodeAtom } from '@root/entities/referral/atoms'
import { tradeSettingsAtom } from '@root/features/trade/trade-settings/atoms/reatom-trade-settings'
import {
  tradingPairAtom,
  tradingWalletAtom,
} from '@root/features/trade/trading/atoms'
import { NumberUtil } from '@root/shared'
import { reatomAsyncState } from '@root/shared/lib'

import type { Atom } from '@reatom/framework'
import type { Quote } from '@root/entities/quote'
import type { NotEnoughLiquidityError } from '@root/entities/quote/atoms'
import type { SwapDirection } from '@root/entities/trade-type/model/trade-type'
import type { Error } from '@root/shared'
import type { ApiError } from '@root/shared/api'
import type BigNumber from 'bignumber.js'

const INPUT_DEBOUNCE = 300

export type QuoteInput = {
  amount: BigNumber
  direction: SwapDirection
}

export const reatomControlsQuote = (args: {
  name?: string
  amountAtom: Atom<BigNumber>
  direction: SwapDirection
}) => {
  const { name = 'unknown', amountAtom, direction } = args

  const retryTriggerAtom = atom(nanoid())

  const quoteStateAtom = reatomAsyncState.notInitiated<
    Error.UnknownError | ApiError.ApiError<string> | NotEnoughLiquidityError,
    Quote.Quote
  >(`${name}.quoteStateAtom`)

  const refreshAction = reatomAsync(async (ctx) => {
    const pair = ctx.get(tradingPairAtom)
    const amount = ctx.get(amountAtom)
    const wallet = ctx.get(tradingWalletAtom)

    if (!pair || amount.isZero()) {
      return
    }

    const settings = ctx.get(tradeSettingsAtom)
    const currencyIn = direction === 'buy' ? pair.base : pair.quote
    const currencyOut = direction === 'buy' ? pair.quote : pair.base

    const quoteResult = await ctx.schedule(() =>
      fetchQuoteRequestAction(ctx, {
        currencyIn: currencyIn,
        currencyOut: currencyOut,
        amount: amount,
        type: 'EXACT_INPUT',
        poolAddress: pair.address,
        providerId: pair.provider.id,
        settings,
        network: pair.network,
        wallet: wallet,
        inviteCode: ctx.get(joinedInviteCodeAtom),
      }),
    )

    if (Result.isSuccess(quoteResult)) {
      quoteStateAtom.setSuccess(ctx, quoteResult.success)
    }
  }).pipe(withAbort())

  const prevAmountAtom = atom<BigNumber | undefined>(undefined)
  const loadReaction = reaction(async (ctx) => {
    refreshAction.abort(ctx, 'reaction')
    const pair = ctx.spy(tradingPairAtom)
    const prevAmount = ctx.get(prevAmountAtom)
    const amount = ctx.spy(amountAtom)
    const wallet = ctx.spy(tradingWalletAtom)
    const _retryTrigger = ctx.spy(retryTriggerAtom)
    prevAmountAtom(ctx, amount)

    if (!pair || amount.isZero()) {
      quoteStateAtom.setNotInitiated(ctx)
      return
    }
    const settings = ctx.spy(tradeSettingsAtom)

    quoteStateAtom.setLoading(ctx)

    // Debounce
    if (prevAmount && !prevAmount.eq(amount) && !prevAmount.isZero()) {
      await ctx.schedule(() => sleep(INPUT_DEBOUNCE))
    }

    const currencyIn = direction === 'buy' ? pair.base : pair.quote
    const currencyOut = direction === 'buy' ? pair.quote : pair.base

    const quoteResult = await ctx.schedule(() =>
      fetchQuoteRequestAction(ctx, {
        currencyIn: currencyIn,
        currencyOut: currencyOut,
        amount: amount,
        type: 'EXACT_INPUT',
        poolAddress: pair.address,
        providerId: pair.provider.id,
        settings,
        network: pair.network,
        wallet: wallet,
        inviteCode: ctx.get(joinedInviteCodeAtom),
      }),
    )

    quoteStateAtom.setResult(ctx, quoteResult)
  })

  const forceRetryAction = action((ctx) => {
    retryTriggerAtom(ctx, nanoid())
  })

  onConnect(quoteStateAtom, (ctx) => {
    const loadSub = loadReaction(ctx)
    const unsubNewTx = newTxEvent.onCall((ctx, newTx) => {
      const pair = ctx.get(tradingPairAtom)
      if (!pair || newTx.pairAddress !== pair.address) {
        return
      }

      refreshAction(ctx)
    })

    return () => {
      loadSub.unsubscribe()
      unsubNewTx()
    }
  })

  const priceImpactAtom = atom((ctx) => {
    const quoteState = ctx.spy(quoteStateAtom)

    return pipe(
      quoteState,
      AsyncStateN.map((it) => NumberUtil.safeBigNumber(it.priceImpact, 0)),
    )
  }, `${name}.priceImpactAtom`)

  const outputAmountAtom = atom((ctx) => {
    const quoteState = ctx.spy(quoteStateAtom)

    return pipe(
      quoteState,
      AsyncStateN.map((it) => it.outputAmount),
    )
  }, `${name}.outputAmountAtom`)

  return {
    quoteStateAtom,
    priceImpact: priceImpactAtom,
    outputAmount: outputAmountAtom,
    forceRetry: forceRetryAction,
  }
}

export type ControlsQuoteModel = ReturnType<typeof reatomControlsQuote>
